import { api } from "../../shared/api.ts";
import JSZip from "jszip";

export interface BacktestBackup {
  format: "autocosting-backtest-backup-v1"; source: string;
  cases: { id: string; parts?: unknown[]; file?: string | null; payload?: string }[];
  runs: { id: string }[];
  results: { run_id: string; case_id: string }[];
}
/** Per-case requests avoid uploading the whole library in one oversized HTTP request. Retrying is safe. */
export async function importBacktestBackup(file: File, progress: (message: string) => void) {
  let text: string;
  let archive: JSZip | null = null;
  if (/\.gz$/i.test(file.name)) {
    text = await new Response(file.stream().pipeThrough(new DecompressionStream("gzip"))).text();
  } else if (/\.zip$/i.test(file.name)) {
    archive = await JSZip.loadAsync(await file.arrayBuffer());
    const manifest = archive.file("manifest.json") || archive.file("production-backtest.json");
    if (!manifest) throw new Error("Missing backup manifest");
    text = await manifest.async("string");
  } else text = await file.text();
  const data = JSON.parse(text) as BacktestBackup;
  text = "";
  if (data.format !== "autocosting-backtest-backup-v1" || !data.source || !Array.isArray(data.cases) || !Array.isArray(data.runs) || !Array.isArray(data.results)) throw new Error("Invalid backtest backup");
  const ids = new Set(data.cases.map((c) => c.id)), runIds = new Set(data.runs.map((r) => r.id));
  if (ids.size !== data.cases.length || runIds.size !== data.runs.length || data.cases.some((c) => c.payload ? !archive?.file(c.payload) : !Array.isArray(c.parts) || !c.parts.length)
    || data.results.some((r) => !ids.has(r.case_id) || !runIds.has(r.run_id))) throw new Error("Backup has missing or duplicate cases / runs / file contents");
  let cases = 0, runs = 0;
  for (const [i, c] of data.cases.entries()) {
    progress(`Techpack ${i + 1}/${data.cases.length}`);
    let payload: { case: { id: string }; parts: unknown[]; file: string | null };
    if (c.payload) {
      payload = JSON.parse(await archive!.file(c.payload)!.async("string"));
      if (payload.case?.id !== c.id || !Array.isArray(payload.parts) || !payload.parts.length || (payload.file !== null && typeof payload.file !== "string")) throw new Error("Invalid case payload");
    } else {
      const { parts, file, ...row } = c;
      payload = { case: row, parts: parts!, file: file ?? null };
    }
    const result = await api.btImportCase({ source: data.source, ...payload });
    if (!result.skipped) cases++;
  }
  for (const [i, r] of data.runs.entries()) {
    progress(`Backtest ${i + 1}/${data.runs.length}`);
    const result = await api.btImportRun({ source: data.source, run: r, results: data.results.filter((x) => x.run_id === r.id) });
    if (!result.skipped) runs++;
  }
  return { cases, runs };
}
